import { useEffect, useState, useRef } from 'react';
import { List, ChevronRight } from 'lucide-react';

/**
 * Sticky table of contents for long-form blog posts.
 * - Desktop: fixed sticky sidebar on the right of the article
 * - Mobile: collapsible dropdown at the top of the article
 * Highlights the currently visible section using IntersectionObserver.
 */
export default function TableOfContents({ sections = [] }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const [mobileOpen, setMobileOpen] = useState(false);
  const observedRef = useRef(new Map());

  useEffect(() => {
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost intersecting section
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );

    sections.forEach((sec) => {
      const el = document.getElementById(sec.id);
      if (el) {
        observer.observe(el);
        observedRef.current.set(sec.id, el);
      }
    });

    return () => observer.disconnect();
  }, [sections]);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const yOffset = -96; // account for sticky-ish header spacing
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setMobileOpen(false);
  };

  if (!sections.length) return null;

  return (
    <>
      {/* Desktop sticky sidebar */}
      <aside
        data-testid='blog-toc-desktop'
        className='hidden lg:block'
        aria-label='Table of contents'
      >
        <div className='sticky top-28'>
          <div className='rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur px-5 py-5'>
            <div className='flex items-center gap-2 text-white/60 uppercase tracking-[0.15em] text-[11px] font-accent mb-4'>
              <List className='w-3.5 h-3.5' />
              On this page
            </div>
            <nav className='space-y-1.5 max-h-[70vh] overflow-y-auto pr-2 -mr-2'>
              {sections.map((sec, i) => {
                const isActive = sec.id === activeId;
                return (
                  <a
                    key={sec.id}
                    href={`#${sec.id}`}
                    onClick={(e) => handleClick(e, sec.id)}
                    data-testid={`toc-link-${sec.id}`}
                    className={`block text-[13px] leading-[1.55] py-1.5 pl-3 border-l-2 transition-colors ${
                      isActive
                        ? 'border-brand text-white font-medium'
                        : 'border-white/10 text-white/55 hover:text-white/85 hover:border-white/30'
                    }`}
                  >
                    <span className='opacity-60 mr-1.5'>{String(i + 1).padStart(2, '0')}</span>
                    {sec.label}
                  </a>
                );
              })}
            </nav>
          </div>
        </div>
      </aside>

      {/* Mobile collapsible ToC */}
      <div className='lg:hidden mb-6' data-testid='blog-toc-mobile'>
        <button
          type='button'
          onClick={() => setMobileOpen((v) => !v)}
          className='w-full flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur px-4 py-3 text-white'
          data-testid='blog-toc-mobile-toggle'
        >
          <span className='inline-flex items-center gap-2 text-[13px] font-medium'>
            <List className='w-4 h-4' />
            On this page
            <span className='text-white/50'>({sections.length})</span>
          </span>
          <ChevronRight className={`w-4 h-4 transition-transform ${mobileOpen ? 'rotate-90' : ''}`} />
        </button>
        {mobileOpen && (
          <div className='mt-2 rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur px-3 py-3'>
            <nav className='space-y-1'>
              {sections.map((sec, i) => (
                <a
                  key={sec.id}
                  href={`#${sec.id}`}
                  onClick={(e) => handleClick(e, sec.id)}
                  className={`block text-[13px] py-1.5 px-2 rounded-md ${
                    sec.id === activeId ? 'bg-brand/15 text-white' : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <span className='opacity-60 mr-1.5'>{String(i + 1).padStart(2, '0')}</span>
                  {sec.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </div>
    </>
  );
}
